import { authenticatedMiddleware } from '../../permissions/middlewares/authenticatedMiddleware.js';
import { checkPermissionMiddleware } from '../../permissions/middlewares/checkPermissionMiddleware.js';
import { Permission } from '../../permissions/roles.js';
import { GLOBAL_CONTEXT } from '@adornis/users/db/a-roles.js';
import { ID } from '@adornis/baseql/baseqlTypes.js';
import { registerMutation, registerQuery } from '@adornis/baseql/metadata/register.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import { toCalendarDay, todayAsCalendarDay } from '@adornis/datetime/calendarDay.js';
import { addMonths, startOfMonth } from 'date-fns';
import { JobTicketWrapper } from '../../db/job-ticket-wrapper.js';
import { Person } from '../../users/entities/person.js';
import { generateTicketCSV } from '../server/generateTicketCSV.js';
import { saveJobTicketWrapperData } from '../server/saveJobTicketWrapperData.js';
import { sendTicketInviteMail } from '../server/sendTicketInviteMail.js';
import { sendTicketOrderedMail } from '../server/sendTicketOrderedMail.js';

const getTicketOpen = registerQuery({
  operationName: 'getTicketOpen',
  middlewares: [authenticatedMiddleware],
  type: () => Boolean,
  params: [{ type: () => ID, name: 'userID' }],

  resolve: (userID: string) => async () => {
    const userCol = await getCollection<Person>(Person._class);
    const user = await userCol.findOne({ _id: userID });
    if (!user) return false;

    // the cancellation date is a canonical month start, so it is compared against today's day
    return (
      user.mitarbeiterDaten?.jobTicketBestaetigt &&
      (!user.mitarbeiterDaten.jobTicketKuendigungsDatum ||
        user.mitarbeiterDaten.jobTicketKuendigungsDatum > todayAsCalendarDay())
    );
  },
});

const closeTicket = registerMutation({
  operationName: 'closeTicket',
  middlewares: [authenticatedMiddleware],
  type: () => Boolean,
  params: [
    { type: () => ID, name: 'userID' },
    { type: () => Date, name: 'date' },
  ],

  resolve: (userID: string, date: Date) => async () => {
    const userCol = await getCollection<Person>(Person._class);
    const user = await userCol.findOne({ _id: userID });
    if (!user) return false;

    // the cancellation takes effect with the following month — stored as canonical UTC month start
    const day = toCalendarDay(date);
    await userCol.updateOne(
      { _id: userID },
      {
        $set: {
          'mitarbeiterDaten.jobTicketKuendigungsDatum': startOfMonth(addMonths(day, 1)),
        },
      },
    );
    return true;
  },
});

const inviteToTicket = registerQuery({
  operationName: 'sendTicketInviteMail',
  middlewares: [authenticatedMiddleware],
  type: () => Boolean,
  params: [{ type: () => String, name: 'mitarbeiterID' }],
  options: { requestHandlerMeta: { queryCache: { cachePolicy: 'bypass' } } },

  resolve: (mitarbeiterID: string) => () => sendTicketInviteMail(mitarbeiterID),
});

const notifyTicketOrdered = registerQuery({
  operationName: 'sendTicketOrderedMail',
  middlewares: [authenticatedMiddleware],
  type: () => Boolean,
  params: [{ type: () => String, name: 'personID' }],
  options: { requestHandlerMeta: { queryCache: { cachePolicy: 'bypass' } } },

  resolve: (personID: string) => () => sendTicketOrderedMail(personID),
});

const saveTicketData = registerMutation({
  operationName: 'saveJobTicketWrapperData',
  middlewares: [authenticatedMiddleware],
  type: () => Boolean,
  params: [
    { type: () => ID, name: 'personID' },
    { type: () => JobTicketWrapper, name: 'jobTicketWrapper' },
  ],

  resolve: (personID: string, jobTicketWrapper: JobTicketWrapper) => () =>
    saveJobTicketWrapperData(personID, jobTicketWrapper),
});

/** @returns the URL the generated CSV can be downloaded from */
const downloadTicketCSV = registerMutation({
  operationName: 'downloadTicketCSV',
  middlewares: [checkPermissionMiddleware(Permission.EXPORT_TICKET, GLOBAL_CONTEXT)],
  type: () => String,
  options: { requestHandlerMeta: { queryCache: { cachePolicy: 'bypass' } } },

  resolve: () => () => generateTicketCSV(),
});

export const TicketAPI = {
  isOpen: getTicketOpen,
  close: closeTicket,
  invite: inviteToTicket,
  notifyOrdered: notifyTicketOrdered,
  save: saveTicketData,
  downloadCSV: downloadTicketCSV,
};
